/**
 * Lightbox — full-screen media viewer. Opens when a user clicks a post's image
 * (or the expand button on a video) to see the full, uncropped media. Closes on
 * Esc, backdrop click, or the X button. Clicking the media itself does NOT
 * close the lightbox (stopPropagation) so the user can interact with a video's
 * controls. Self-contained, additive — does not modify any page that uses it.
 *
 * Rendered with `position: fixed` so it escapes any parent `overflow-hidden`
 * container (e.g. the post card's rounded article).
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'

interface LightboxProps {
  src: string
  mediaType?: 'IMAGE' | 'VIDEO'
  onClose: () => void
}

const Lightbox = ({ src, mediaType, onClose }: LightboxProps) => {
  // Esc to close + lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4 animate-[fadeIn_0.15s_ease-out]"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 p-2 text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      {mediaType === 'VIDEO' ? (
        <video
          src={src}
          controls
          autoPlay
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={src}
          alt=""
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  )
}

export default Lightbox